import React, { useState } from "react";
import {
  Form,
  Input,
  Radio,
  Checkbox,
  Button,
  Row,
  Col,
  message,
  Spin,
  Select,
} from "antd";
import "./DiabetesScreeningForm.css";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Country, State, City } from "country-state-city";

const DiabetesScreeningForm = ({ onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [emergencyCountryCode, setEmergencyCountryCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const cleanPhone = (number, dialCode) =>
    number.startsWith(dialCode) ? number.slice(dialCode.length) : number;

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const payload = {
        name: values.fullName,
        country_code: countryCode,
        phone: cleanPhone(
          values.mobileNumber,
          values.mobileNumber.slice(0, values.mobileNumber.length - 10),
        ),
        email: values.email,
        country: Country.getCountryByCode(values.country)?.name || values.country,
        state: State.getStateByCodeAndCountry(values.state, values.country)?.name || values.state,
        city: values.city,
        neighborhood: values.neighborhood,
        age_range: values.ageRange,
        gender: values.gender,
        allergies:
          values.foodAllergy || values.medicationAllergy
            ? values.allergyDetails || "Yes"
            : "None",
        sugar_kidneytest: values.lastBloodTest,
        medication_sugar_pressure: values.onMedication,
        emergency_con_name: values.emergencyContactName,
        emergency_country_code: emergencyCountryCode,
        emergency_phone: cleanPhone(
          values.emergencyContactPhone,
          values.emergencyContactPhone.slice(
            0,
            values.emergencyContactPhone.length - 10,
          ),
        ),
        health_check: (values.interestReasons || []).join(", "),
        comm_preference: (values.communicationPreferences || []).join(", "),
        health_concern: values.healthConcern,
        screening_date: values.screeningDate,
      };

      const formData = new FormData();
      formData.append("ketnyproduct_payload", JSON.stringify(payload));

      await axios.post(
        "https://verimedramja.pythonanywhere.com/verimed/ketnyproductioncheck_c/",
        formData,
      );

      message.success("Form submitted successfully!");

      form.resetFields();
      setLoading(false);
      onClose();
    } catch (error) {
      console.error("Error:", error);
      message.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const ageRangeOptions = [
    { label: "18–35", value: "18-35" },
    { label: "36–50", value: "36-50" },
    { label: "51–65", value: "51-65" },
    { label: "66+", value: "66+" },
  ];

  const genderOptions = [
    { label: "Female", value: "female" },
    { label: "Male", value: "male" },
  ];

  const lastTestOptions = [
    { label: "Within the last 6 months", value: "6months" },
    { label: "6–12 months ago", value: "6-12months" },
    { label: "Over 1 year ago", value: "over1year" },
    { label: "I've never tested", value: "never" },
    { label: "I don't remember", value: "dontremember" },
  ];

  const medicationOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const screeningDateOptions = [
    { label: "Saturday August 8th (GRA)", value: "Saturday August 8th (GRA)" },
    { label: "Sunday August 9th", value: "Sunday August 9th" },
    { label: "Saturday August 15th (GRA)", value: "Saturday August 15th (GRA)" },
    { label: "Sunday August 16th", value: "Sunday August 16th" },
    { label: "Saturday August 22nd (GRA)", value: "Saturday August 22nd (GRA)" },
    { label: "Sunday August 23rd", value: "Sunday August 23rd" },
    { label: "Saturday August 29th", value: "Saturday August 29th" },
    { label: "Sunday August 30th (GRA)", value: "Sunday August 30th (GRA)" },
    { label: "Saturday September 5th", value: "Saturday September 5th" },
    { label: "Sunday September 6th", value: "Sunday September 6th" },
    { label: "Saturday September 12th", value: "Saturday September 12th" },
    { label: "Sunday September 13th", value: "Sunday September 13th" },
    { label: "Saturday September 19th", value: "Saturday September 19th" },
    { label: "Sunday September 20th", value: "Sunday September 20th" },
    { label: "Saturday September 26th", value: "Saturday September 26th" },
    { label: "Sunday September 27th", value: "Sunday September 27th" },
    { label: "Saturday October 3rd", value: "Saturday October 3rd" },
    { label: "Sunday October 4th", value: "Sunday October 4th" },
    { label: "Saturday October 10th", value: "Saturday October 10th" },
    { label: "Sunday October 11th", value: "Sunday October 11th" },
    { label: "Saturday October 17th", value: "Saturday October 17th" },
    { label: "Sunday October 18th", value: "Sunday October 18th" },
    { label: "Saturday October 24th", value: "Saturday October 24th" },
    { label: "Sunday October 25th", value: "Sunday October 25th" },
  ];

  const interestOptions = [
    { label: "I have diabetes", value: "diabetes" },
    {
      label: "I was told I have high blood pressure",
      value: "highbloodpressure",
    },
    { label: "I have kidney concerns", value: "kidneyconcerns" },
    {
      label: "A family member has diabetes or kidney disease",
      value: "familyhistory",
    },
    { label: "I want to prevent future health problems", value: "prevention" },
    { label: "I just want to know my health status", value: "healthstatus" },
    { label: "Other", value: "other" },
  ];

  const communicationOptions = [
    { label: "WhatsApp", value: "whatsapp" },
    { label: "SMS", value: "sms" },
    { label: "Email", value: "email" },
    { label: "Inside the VeriMED app", value: "app" },
    { label: "No", value: "no" },
  ];

  return (
    <Spin spinning={loading}>
      <div className="diabetes-screening-form">
        <div className="form-header">
          <h2>Free Diabetes Screening & Kidney Protection Check</h2>
          <p>Please fill out this form to book your health check</p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          className="diabetes-form-container"
        >
          {/* Section 1: Personal Information */}
          <div className="form-section">
            <h3>Personal Information</h3>

            <Form.Item
              label="Full Name"
              name="fullName"
              rules={[
                { required: true, message: "Please enter your full name" },
              ]}
            >
              <Input placeholder="Enter your full name" />
            </Form.Item>

            <Form.Item
              label="Mobile Number (WhatsApp preferred)"
              name="mobileNumber"
              rules={[
                { required: true, message: "Please enter your mobile number" },
              ]}
            >
              <PhoneInput
                country={"cm"}
                enableSearch={true}
                inputStyle={{ width: "100%" }}
                value={form.getFieldValue("mobileNumber")}
                onChange={(value, data) => {
                  form.setFieldsValue({ mobileNumber: value });
                  setCountryCode(data.countryCode.toUpperCase());
                }}
              />
            </Form.Item>

            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: "Please enter your email address" },
                {
                  type: "email",
                  message: "Please enter a valid email address",
                },
              ]}
            >
              <Input placeholder="Enter your email address" type="email" />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Country"
                  name="country"
                  rules={[{ required: true, message: "Please select your country" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select Country"
                    options={Country.getAllCountries().map((c) => ({
                      label: c.name,
                      value: c.isoCode,
                    }))}
                    onChange={(value) => {
                      setSelectedCountry(value);
                      form.setFieldsValue({ state: undefined, city: undefined });
                      setSelectedState("");
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="State/Region"
                  name="state"
                  rules={[{ required: true, message: "Please select your state/region" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select State/Region"
                    options={State.getStatesOfCountry(selectedCountry).map((s) => ({
                      label: s.name,
                      value: s.isoCode,
                    }))}
                    onChange={(value) => {
                      setSelectedState(value);
                      form.setFieldsValue({ city: undefined });
                    }}
                    disabled={!selectedCountry}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: "Please select your city" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select City"
                    options={City.getCitiesOfState(selectedCountry, selectedState).map((c) => ({
                      label: c.name,
                      value: c.name,
                    }))}
                    disabled={!selectedState}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Neighborhood"
                  name="neighborhood"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your neighborhood",
                    },
                  ]}
                >
                  <Input placeholder="Enter your neighborhood" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Age Range"
                  name="ageRange"
                  rules={[
                    { required: true, message: "Please select your age range" },
                  ]}
                >
                  <Radio.Group options={ageRangeOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Gender"
                  name="gender"
                  rules={[
                    { required: true, message: "Please select your gender" },
                  ]}
                >
                  <Radio.Group options={genderOptions} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Section 2: Health Information */}
          <div className="form-section">
            <h3>Health Information</h3>

            <Form.Item label="Allergies">
              <div className="allergy-section">
                <Form.Item name="foodAllergy" valuePropName="checked" noStyle>
                  <Checkbox>Food Allergy</Checkbox>
                </Form.Item>
                <Form.Item
                  name="allergyDetails"
                  noStyle
                  style={{ marginLeft: "20px", marginTop: "8px" }}
                >
                  <Input
                    placeholder="Specify allergies (if any)"
                    style={{ display: "none" }}
                  />
                </Form.Item>
              </div>
              <div style={{ marginTop: "10px" }}>
                <Form.Item
                  name="medicationAllergy"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Medication Allergy</Checkbox>
                </Form.Item>
              </div>
              <div style={{ marginTop: "10px" }}>
                <Form.Item name="noAllergy" valuePropName="checked" noStyle>
                  <Checkbox>No Known Allergy</Checkbox>
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label="Last Blood Sugar or Kidney Test"
              name="lastBloodTest"
              rules={[
                {
                  required: true,
                  message: "Please select when you last had a test",
                },
              ]}
            >
              <Radio.Group options={lastTestOptions} />
            </Form.Item>

            <Form.Item
              label="Currently on medication for blood sugar or blood pressure?"
              name="onMedication"
              rules={[{ required: true, message: "Please select an option" }]}
            >
              <Radio.Group options={medicationOptions} />
            </Form.Item>
          </div>

          {/* Screening Date & Location */}
          <div className="form-section">
            <h3>Screening Date and Location</h3>
            <p className="section-description" style={{ marginBottom: "16px" }}>
              Screening dates and location available.
              <br />
              Please select only one screening date/location from the list below.
            </p>
            <Form.Item
              name="screeningDate"
              rules={[
                {
                  required: true,
                  message: "Please select a screening date and location",
                },
              ]}
            >
              <Radio.Group style={{ width: "100%" }}>
                <Row gutter={[16, 12]}>
                  {screeningDateOptions.map((option) => (
                    <Col xs={24} sm={12} key={option.value}>
                      <Radio value={option.value}>{option.label}</Radio>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Form.Item>
          </div>

          {/* Section 3: Emergency Contact */}
          <div className="form-section">
            <h3>Emergency Contact</h3>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Emergency Contact Name"
                  name="emergencyContactName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your contact number",
                    },
                  ]}
                >
                  <Input placeholder="Enter contact name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Emergency Contact Phone Number"
                  name="emergencyContactPhone"
                  rules={[
                    { required: true, message: "Please enter contact phone" },
                  ]}
                >
                  <PhoneInput
                    country={"cm"}
                    enableSearch={true}
                    inputStyle={{ width: "100%" }}
                    value={form.getFieldValue("emergencyContactPhone")}
                    onChange={(value, data) => {
                      form.setFieldsValue({ emergencyContactPhone: value });
                      setEmergencyCountryCode(data.countryCode.toUpperCase());
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Section 4: Reason for Screening */}
          <div className="form-section">
            <h3>Why are you interested in this Health Check?</h3>

            <Form.Item
              name="interestReasons"
              rules={[
                {
                  required: true,
                  message: "Please select at least one reason",
                },
              ]}
            >
              <Checkbox.Group options={interestOptions} />
            </Form.Item>
          </div>

          {/* Section 5: Communication Preference */}
          <div className="form-section">
            <h3>Communication Preference</h3>
            <p className="section-description">
              How would you like to receive personalized health tips and
              reminders from VeriMED?
            </p>

            <Form.Item
              name="communicationPreferences"
              rules={[
                {
                  required: true,
                  message: "Please select your communication preference",
                },
              ]}
            >
              <Checkbox.Group options={communicationOptions} />
            </Form.Item>
          </div>

          {/* Section 6: Health Concern */}
          <div className="form-section">
            <h3>Your Health Concerns</h3>

            <Form.Item
              label="What is your biggest health concern right now?"
              name="healthConcern"
              rules={[
                {
                  required: true,
                  message: "Please share your health concern",
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Please share in 200 words or less"
                maxLength={200}
                showCount
              />
            </Form.Item>
          </div>

          {/* Section 7: Trust & Safety */}
          <div className="form-section trust-section">
            <div className="trust-content">
              <h4>Trust & Safety Statement</h4>
              <p>
                <strong>Your privacy matters.</strong>
              </p>
              <p>
                VeriMED does not sell or share your information. Your details
                are used only to provide your health evaluation and follow-up
                support if you request it.
              </p>
            </div>

            <Form.Item
              name="agreement"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            "Please acknowledge the Privacy Statement and declare that you understand this is a screening and not a full medical diagnosis",
                          ),
                        ),
                },
              ]}
            >
              <Checkbox>
                I understand that this is a screening and not a full medical
                diagnosis.
              </Checkbox>
            </Form.Item>
          </div>

          {/* Submit Buttons */}
          <div className="form-actions">
            <Button
              type="default"
              size="large"
              onClick={onClose}
              className="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="submit-btn"
            >
              Submit Health Screening
            </Button>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default DiabetesScreeningForm;
